import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, X, Printer, Eye } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BookingService } from "@/services/bookingService";
import { BookingRequestWithDetails } from "@/integrations/mysql/types";
import { getCoverImageUrl } from "@/lib/imageMap";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import { getPropertyImageUrl, getPublicUrl } from "@/config/api";

const MyBookings = () => {
  const [bookings, setBookings] = useState<BookingRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<BookingRequestWithDetails | null>(null);
  const { toast } = useToast();
  const { isAuthenticated, userId, user, userType, loading: authLoading } = useAuthState();
  
  // Debug logs
  console.log('🔐 MyBookings - Auth state:', { isAuthenticated, userId, authLoading });

  // Função para formatar datas SEM timezone - usar exatamente como vem do banco
  // Para check_in e check_out (campos DATE): formatar diretamente da string YYYY-MM-DD
  // Para created_at (TIMESTAMP): converter timezone apenas para exibir hora
  const formatDate = (dateString: string | Date | null | undefined, formatString: string = "dd/MM/yyyy"): string => {
    if (!dateString) return "Data não informada";
    
    try {
      // Se for uma data sem hora (check_in, check_out) - vem como string YYYY-MM-DD
      // Formatar diretamente da string SEM criar Date object que aplica timezone
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString) && !formatString.includes('HH:mm') && !formatString.includes('às')) {
        // Formatar diretamente da string YYYY-MM-DD para DD/MM/YYYY
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // Para datas com hora (created_at) - precisa converter timezone
      let date: Date;
      
      if (typeof dateString === 'string') {
        // Tentar parseISO primeiro para datas ISO
        date = parseISO(dateString);
        if (!isValid(date)) {
          // Se não for ISO válida, tentar new Date
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }
      
      if (!isValid(date)) {
        console.warn('Data inválida:', dateString);
        return "Data inválida";
      }
      
      // Para datas com hora, converter para timezone de Brasília
      // O MySQL salva TIMESTAMP em UTC, então precisamos subtrair 3 horas para Brasília
      if (formatString.includes('HH:mm') || formatString.includes('às')) {
        // Converter UTC para Brasília (UTC-3)
        const utcTime = date.getTime();
        const brasiliaTime = utcTime - (3 * 60 * 60 * 1000); // Subtrair 3 horas
        const brasiliaDate = new Date(brasiliaTime);
        
        // Obter componentes formatados
        const day = brasiliaDate.getDate().toString().padStart(2, '0');
        const month = (brasiliaDate.getMonth() + 1).toString().padStart(2, '0');
        const year = brasiliaDate.getFullYear();
        const hour = brasiliaDate.getHours().toString().padStart(2, '0');
        const minute = brasiliaDate.getMinutes().toString().padStart(2, '0');
        
        // Se o formato inclui 'às', usar formato brasileiro
        if (formatString.includes('às')) {
          return `${day}/${month}/${year} às ${hour}:${minute}`;
        }
        
        return format(brasiliaDate, formatString, { locale: ptBR });
      }
      
      // Para datas sem hora que não são YYYY-MM-DD, usar date-fns
      // Mas extrair apenas a parte da data se for uma string ISO
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const datePart = dateString.substring(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          const [year, month, day] = datePart.split('-');
          return `${day}/${month}/${year}`;
        }
      }
      
      return format(date, formatString, { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return "Erro na data";
    }
  };

  useEffect(() => {
    console.log('🔄 MyBookings useEffect - Auth state:', { authLoading, isAuthenticated, userId });
    
    if (!authLoading) {
      if (isAuthenticated && userId) {
        console.log('✅ Usuário autenticado, carregando reservas...');
        loadBookings();
      } else if (!isAuthenticated) {
        console.log('❌ Usuário não autenticado, redirecionando...');
        window.location.href = "/login";
      } else {
        console.log('⚠️ Usuário autenticado mas sem userId');
      }
    }
  }, [authLoading, isAuthenticated, userId]);

  // Aguardar carregamento da autenticação
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando autenticação...</p>
    </div>
    </div>
    );
  }

  // Redirecionar se não estiver autenticado
  if (!isAuthenticated) {
    return null;
  }

  const loadBookings = async () => {
    console.log('🚀 loadBookings chamada com userId:', userId, 'tipo:', typeof userId);
    
    try {
      if (!userId) {
        console.log('❌ userId não disponível, cancelando carregamento');
        return;
      }

      // Garantir que userId seja um número
      const numericUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
      
      if (isNaN(numericUserId)) {
        console.error('❌ userId inválido:', userId);
        return;
      }

      console.log('📋 Carregando reservas para usuário:', numericUserId);
      setLoading(true);
      
      const userBookings = await BookingService.getBookingRequestsByGuest(numericUserId);
      console.log('📋 Reservas carregadas:', userBookings.length, 'reservas');
      
      // Filtrar apenas reservas do usuário atual (segurança adicional)
      const filteredBookings = userBookings.filter((b) => b.guest_id === numericUserId);
      
      // Remover duplicatas por ID (caso o GROUP BY não tenha funcionado)
      const uniqueBookings = filteredBookings.filter((booking, index, self) => 
        index === self.findIndex(b => b.id === booking.id)
      );
      
      console.log('📋 Reservas filtradas (apenas do usuário):', uniqueBookings.length);
      
      if (uniqueBookings.length !== filteredBookings.length) {
        console.warn('⚠️ Duplicatas removidas!', {
          total: filteredBookings.length,
          únicas: uniqueBookings.length,
          removidas: filteredBookings.length - uniqueBookings.length
        });
      }
      
      setBookings(uniqueBookings);
    } catch (error: any) {
      console.error('❌ Erro ao carregar reservas:', error);
      toast({
        title: "Erro ao carregar reservas",
        description: error.message || "Não foi possível carregar suas reservas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (booking: BookingRequestWithDetails) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      console.log('❌ Cancelando reserva:', bookingToCancel.id);
      await BookingService.cancelBookingRequest(bookingToCancel.id!, "Cancelado pelo hóspede");
      
      // Recarregar as reservas
      await loadBookings();

      toast({
        title: "Reserva cancelada",
        description: "Sua reserva foi cancelada com sucesso.",
      });
    } catch (error: any) {
      console.error('❌ Erro ao cancelar reserva:', error);
      toast({
        title: "Erro ao cancelar",
        description: error.message || "Não foi possível cancelar a reserva",
        variant: "destructive",
      });
    } finally {
      setBookingToCancel(null);
    }
  };

  const printBooking = (booking: BookingRequestWithDetails) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // URL absoluta para a logo
    const logoUrl = getPublicUrl('public/live-stay-logo.png');
    
    // URL direta para a imagem da propriedade
    const propertyImageUrl = getPropertyImageUrl(booking.property_id, booking.cover_image);
    
    // Debug: Log da URL da imagem
    console.log('URL da imagem da propriedade:', propertyImageUrl);
    console.log('property_id:', booking.property_id);
    console.log('cover_image:', booking.cover_image);

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Comprovante de Reserva - ${booking.property_title}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            
            body { 
              font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif; 
              line-height: 1.6; 
              color: #334155; 
              background: #ffffff;
              font-size: 14px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .container { 
              max-width: 210mm; 
              margin: 0 auto; 
              background: white;
              min-height: 297mm;
              position: relative;
              padding: 0;
            }
            
            .header { 
              background: linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%);
              color: white;
              padding: 18px 25px;
              position: relative;
              display: flex;
              align-items: center;
              justify-content: space-between;
              border-radius: 0 0 16px 16px;
              box-shadow: 0 4px 16px rgba(13, 148, 136, 0.2);
              margin-bottom: 18px;
            }
            
            .logo-section {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .logo {
              width: 80px;
              height: 50px;
              object-fit: contain;
              background: white;
              border-radius: 10px;
              padding: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
            }
            
            .header-text h1 { 
              font-size: 20px; 
              font-weight: 700;
              margin-bottom: 3px;
              text-shadow: 0 1px 2px rgba(0,0,0,0.15);
              letter-spacing: -0.2px;
            }
            
            .header-text .subtitle {
              font-size: 11px;
              opacity: 0.9;
              font-weight: 400;
              letter-spacing: 0.1px;
            }
            
            .status-badge {
              background: rgba(255, 255, 255, 0.2);
              backdrop-filter: blur(10px);
              padding: 6px 14px;
              border-radius: 16px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid rgba(255, 255, 255, 0.3);
              box-shadow: 0 1px 4px rgba(0,0,0,0.06);
            }
            
            .main-content {
              padding: 0 25px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            }
            
            .property-image {
              grid-column: 1 / -1;
              width: 100%;
              height: 120px;
              object-fit: cover;
              border-radius: 8px;
              margin: 0 0 16px 0;
              display: block;
              box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            }
            
            .section { 
              padding: 14px; 
              border: 1px solid #e2e8f0; 
              border-radius: 8px;
              background: #ffffff;
              box-shadow: 0 1px 4px rgba(0,0,0,0.03);
            }
            
            .section h3 { 
              margin-bottom: 10px; 
              color: #0d9488; 
              font-size: 13px;
              font-weight: 600;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 6px;
              display: flex;
              align-items: center;
              gap: 6px;
              letter-spacing: 0.1px;
            }
            
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 6px 0; 
              padding: 4px 0;
              border-bottom: 1px solid #f1f5f9;
              font-size: 12px;
            }
            
            .info-row:last-child { 
              border-bottom: none; 
            }
            
            .label { 
              font-weight: 500; 
              color: #64748b;
              min-width: 90px;
              font-size: 11px;
              text-transform: none;
              letter-spacing: 0.05px;
            }
            
            .value { 
              color: #1e293b;
              text-align: right;
              flex: 1;
              font-weight: 500;
              font-size: 12px;
              line-height: 1.4;
            }
            
            .price-section {
              grid-column: 1 / -1;
              background: #f0fdfa;
              color: #0d9488;
              padding: 12px 16px;
              border-radius: 8px;
              text-align: center;
              font-size: 18px;
              font-weight: 700;
              margin: 12px 0;
              box-shadow: 0 2px 8px rgba(13, 148, 136, 0.08);
              border: 1px solid #a7f3d0;
              letter-spacing: 0.2px;
            }
            
            .notes-section {
              grid-column: 1 / -1;
              background: #fffbeb;
              border: 1px solid #fde68a;
              border-radius: 6px;
              padding: 10px 12px;
              margin: 10px 0;
              font-size: 11px;
              box-shadow: 0 1px 4px rgba(245, 158, 11, 0.08);
            }
            
            .notes-section h4 {
              color: #92400e;
              margin-bottom: 4px;
              font-size: 11px;
              font-weight: 600;
              text-transform: none;
              letter-spacing: 0.1px;
            }
            
            .notes-section p {
              color: #78350f;
              font-style: normal;
              line-height: 1.5;
              font-weight: 400;
            }
            
            .footer { 
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              text-align: center; 
              font-size: 9px; 
              color: #64748b;
              background: #f8fafc;
              padding: 10px 25px;
              border-top: 1px solid #e2e8f0;
            }
            
            .footer p { 
              margin: 2px 0; 
              line-height: 1.3;
            }
            
            .footer p:first-child { 
              font-weight: 500; 
              color: #475569; 
              font-size: 9px;
            }
            
            @media print { 
              body { 
                margin: 0; 
                background: white; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .container { 
                box-shadow: none; 
                border-radius: 0; 
                max-width: none;
                width: 100%;
              }
              .header {
                border-radius: 0;
              }
            }
            
            @page {
              size: A4;
              margin: 0;
            }
          </style>
          <script>
            window.addEventListener('load', function() {
              const logoImg = document.querySelector('.logo');
              const propertyImg = document.querySelector('.property-image');
              
              if (logoImg) {
                logoImg.onerror = function() {
                  console.log('Erro ao carregar logo:', this.src);
                  this.style.display = 'none';
                };
              }
              
              if (propertyImg) {
                propertyImg.onerror = function() {
                  console.log('Erro ao carregar imagem da propriedade:', this.src);
                  this.style.display = 'none';
                };
              }
            });
          </script>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo-section">
                <img src="${logoUrl}" alt="Live Stay" class="logo" onerror="this.style.display='none'">
                <div class="header-text">
                  <h1>Comprovante de Reserva</h1>
                  <div class="subtitle">Live Stay - Porto de Galinhas</div>
    </div>
    </div>
              <div class="status-badge">
                ${booking.status === 'confirmed' ? 'CONFIRMADA' : booking.status === 'pending' ? 'PENDENTE' : 'REJEITADA'}
    </div>
    </div>
            
            <div class="main-content">
              <img src="${propertyImageUrl}" alt="${booking.property_title}" class="property-image" onerror="this.style.display='none'">
              
              <div class="section">
                <h3>🏠 Propriedade</h3>
                <div class="info-row">
                  <span class="label">Nome:</span>
                  <span class="value">${booking.property_title}</span>
    </div>
                <div class="info-row">
                  <span class="label">Endereço:</span>
                  <span class="value">${booking.property_address}</span>
    </div>
    </div>
              
              <div class="section">
                <h3>📅 Reserva</h3>
                <div class="info-row">
                  <span class="label">Check-in:</span>
                  <span class="value">${formatDate(booking.check_in)}</span>
    </div>
                <div class="info-row">
                  <span class="label">Check-out:</span>
                  <span class="value">${formatDate(booking.check_out)}</span>
    </div>
                <div class="info-row">
                  <span class="label">Noites:</span>
                  <span class="value">${booking.total_nights}</span>
    </div>
                <div class="info-row">
                  <span class="label">Status:</span>
                  <span class="value">${booking.status === 'confirmed' ? 'CONFIRMADA' : booking.status === 'pending' ? 'PENDENTE' : 'REJEITADA'}</span>
    </div>
    </div>
              
              <div class="section">
                <h3>👤 Hóspede</h3>
                <div class="info-row">
                  <span class="label">Nome:</span>
                  <span class="value">${booking.guest_name}</span>
    </div>
                <div class="info-row">
                  <span class="label">Email:</span>
                  <span class="value">${booking.guest_email}</span>
    </div>
                <div class="info-row">
                  <span class="label">Telefone:</span>
                  <span class="value">${booking.guest_phone || 'Não informado'}</span>
    </div>
    </div>
              
              <div class="section">
                <h3>🏢 Proprietário</h3>
                <div class="info-row">
                  <span class="label">Nome:</span>
                  <span class="value">${booking.owner_name}</span>
    </div>
                <div class="info-row">
                  <span class="label">Email:</span>
                  <span class="value">${booking.owner_email}</span>
    </div>
                <div class="info-row">
                  <span class="label">Telefone:</span>
                  <span class="value">${booking.owner_phone || 'Não informado'}</span>
    </div>
                <div class="info-row">
                  <span class="label">Solicitado:</span>
                  <span class="value">${formatDate(booking.created_at, "dd/MM/yyyy")}</span>
    </div>
    </div>
              
              <div class="price-section">
                <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
                  <span style="font-size: 12px; opacity: 0.8;">Valor Total:</span>
                  <span style="font-size: 20px; font-weight: 700;">R$ ${booking.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                ${booking.discount_amount && booking.discount_amount > 0 ? `
                  <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #a7f3d0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
                    <span style="font-size: 11px; opacity: 0.7;">Desconto:</span>
                    <span style="font-size: 14px; color: #dc2626; font-weight: 600;">
                      -R$ ${booking.discount_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
    </div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #a7f3d0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
                    <span style="font-size: 12px; opacity: 0.8; font-weight: 600;">Valor Final:</span>
                    <span style="font-size: 18px; font-weight: 700; color: #0d9488;">
                      R$ ${(booking.final_price || booking.total_price - booking.discount_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
    </div>
                ` : ''}
    </div>
              
              ${booking.guest_notes ? `
              <div class="notes-section">
                <h4>📝 Observações do Hóspede</h4>
                <p>${booking.guest_notes}</p>
    </div>
              ` : ''}
              
              ${booking.owner_notes ? `
              <div class="notes-section">
                <h4>💬 Resposta do Proprietário</h4>
                <p>${booking.owner_notes}</p>
    </div>
              ` : ''}
              
              ${booking.owner_comment ? `
              <div class="notes-section" style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #93c5fd; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);">
                <h4 style="color: #1e40af;">💬 Comentário do Proprietário</h4>
                <p style="color: #1e3a8a;">${booking.owner_comment}</p>
    </div>
              ` : ''}
    </div>
            
            <div class="footer">
              <p><strong>Reserva solicitada em:</strong> ${formatDate(booking.created_at_brasilia || booking.created_at, "dd/MM/yyyy 'às' HH:mm")}</p>
              <p>Este comprovante foi gerado automaticamente pelo sistema Live Stay</p>
              <p>Para dúvidas, entre em contato: contato@livestay.com.br</p>
    </div>
    </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Pendente", className: "bg-yellow-500" },
      confirmed: { label: "Confirmada", className: "bg-green-500" },
      rejected: { label: "Rejeitada", className: "bg-red-500" },
      cancelled: { label: "Cancelada", className: "bg-gray-500" },
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  if (loading) {
    return <p className="text-center">Carregando reservas...</p>;
  }

  return (
    <MinhaAreaPageShell>
<div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Minhas Reservas</h1>
        <p className="text-muted-foreground">Acompanhe todas as suas solicitações de hospedagem</p>
    </div>

      {bookings.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma reserva encontrada</h3>
            <p className="text-muted-foreground">
              Você ainda não possui reservas. Comece explorando nossas propriedades!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <Card key={booking.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="bg-muted/30 pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex gap-3 flex-1">
                    <img
                      src={getCoverImageUrl(booking)}
                      alt={booking.property_title}
                      className="w-20 h-20 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        console.error('Erro ao carregar imagem:', booking);
                        console.error('URL tentada:', getCoverImageUrl(booking));
                        console.error('property_id:', booking.property_id);
                        console.error('cover_image:', booking.cover_image);
                        console.error('Estrutura completa do booking:', JSON.stringify(booking, null, 2));
                        // Tentar usar a imagem de capa diretamente se disponível
                        if (booking.cover_image) {
                          e.currentTarget.src = getPropertyImageUrl(booking.property_id, booking.cover_image);
                        } else {
                          e.currentTarget.src = '/placeholder.svg';
                        }
                      }}
                    />
                    <div className="flex-1">
                      <CardTitle className="mb-2 text-foreground text-lg">{booking.property_title}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <MapPin className="h-3 w-3" />
                        <span>{booking.property_address}</span>
    </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{booking.total_nights} noites</span>
    </div>
    </div>
    </div>
                  <div>
                    {getStatusBadge(booking.status)}
    </div>
    </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Check-in</p>
                    <p className="text-base font-semibold text-foreground">
                      {formatDate(booking.check_in)}
                    </p>
    </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Check-out</p>
                    <p className="text-base font-semibold text-foreground">
                      {formatDate(booking.check_out)}
                    </p>
    </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {booking.discount_amount && booking.discount_amount > 0 ? 'Valor Final' : 'Valor Total'}
                    </p>
                    <p className="text-xl font-bold text-primary">
                      R$ {(booking.final_price || booking.total_price).toLocaleString('pt-BR')}
                    </p>
                    {booking.discount_amount && booking.discount_amount > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <span className="line-through">R$ {booking.total_price.toLocaleString('pt-BR')}</span>
                        <span className="ml-2 text-red-600 font-semibold">
                          -R$ {booking.discount_amount.toLocaleString('pt-BR')}
                        </span>
    </div>
                    )}
    </div>
    </div>
                
                {booking.guest_notes && (
                  <div className="mb-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Sua Mensagem</p>
                    <p className="text-sm text-foreground">{booking.guest_notes}</p>
    </div>
                )}
                
                {booking.owner_notes && (
                  <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1 uppercase tracking-wider">Resposta do Proprietário</p>
                    <p className="text-sm text-blue-900 dark:text-blue-100">{booking.owner_notes}</p>
    </div>
                )}

                {booking.owner_comment && (
                  <div className="mb-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-1 uppercase tracking-wider flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      Comentário do Proprietário
                    </p>
                    <p className="text-sm text-green-900 dark:text-green-100">{booking.owner_comment}</p>
    </div>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    <p>Solicitado em: {formatDate(booking.created_at_brasilia || booking.created_at, "dd/MM/yyyy 'às' HH:mm")}</p>
    </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/imovel/${booking.property_id}`, '_blank')}
                      className="gap-2 h-8"
                    >
                      <Eye className="h-3 w-3" />
                      Ver Imóvel
                    </Button>
                    
                    {booking.status === "confirmed" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printBooking(booking)}
                        className="gap-2 h-8"
                      >
                        <Printer className="h-3 w-3" />
                        Imprimir
                      </Button>
                    )}
                    
                {booking.status === "pending" && (
                  <Button
                    variant="destructive"
                    size="sm"
                        onClick={() => handleCancelClick(booking)}
                        className="gap-2 h-8"
                  >
                        <X className="h-3 w-3" />
                    Cancelar Reserva
                  </Button>
                )}
    </div>
    </div>
              </CardContent>
            </Card>
          ))}
    </div>
      )}

      <ConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancelar Reserva"
        description={`Tem certeza que deseja cancelar a reserva de "${bookingToCancel?.property_title}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, Cancelar"
        cancelText="Não, Manter"
        onConfirm={confirmCancelBooking}
        variant="destructive"
      />
    </div>
    </MinhaAreaPageShell>
  );
};

export default MyBookings;
